import React from 'react';
import classNames from 'classnames';
import BubbleCommon from '@op/components/BubbleCommon';
import useIntls from '@opm/hooks/useIntls';
import IconHot from '@opm/assets/svg/icon-hot.svg';
import IconHotActive from '@opm/assets/svg/icon-hot-active.svg';
import IconRising from '@opm/assets/svg/icon-rising.svg';
import IconNew from '@opm/assets/svg/icon-new.svg';
import { TStatusTab } from '../types';
import { STATUS_TAB_OPTIONS } from '../constants';
import './StatusTabs.scss';

export interface IStatusTabOption {
  value: TStatusTab;
  text: string;
  textCn: string;
  tips?: { en: string; zh: string; };
}

interface IStatusTabsProps {
  value: TStatusTab;
  onChange: (value: TStatusTab) => void;
  counts: Record<TStatusTab, number>;
  tabOptions?: IStatusTabOption[];
}

/**
 * 各状态对应图标
 * - 默认态：原色彩色图标
 * - 选中态：Hot 用白色描边版，Rising/New 沿用同 SVG（CSS 控制亮度）
 */
const STATUS_ICON_MAP: Record<TStatusTab, { normal: string; active: string; }> = {
  hot: { normal: IconHot, active: IconHotActive },
  rising: { normal: IconRising, active: IconRising },
  new: { normal: IconNew, active: IconNew },
};

/**
 * StatusTabs Hot/Rising/New 状态切换组件（按 Figma 3902_52397 设计实现）
 *
 * 布局：白色容器 + 内部 3 个 Tab，选中态为蓝底（#326FF0）+ 白字
 * - 容器：312×36，圆角 4px
 * - Tab：高 24，圆角 3px，icon 16×16 + 文字 12px Roboto Bold
 * - hover 显示 BubbleCommon tooltip（showIcon={false}）
 */
export default function StatusTabs({
  value,
  onChange,
  counts,
  tabOptions = STATUS_TAB_OPTIONS,
}: IStatusTabsProps) {
  const { intl } = useIntls();

  return (
    <div className="dbom-ct-status-tabs">
      {tabOptions.map((option, index) => {
        const isActive = value === option.value;
        const icon = isActive
          ? STATUS_ICON_MAP[option.value].active
          : STATUS_ICON_MAP[option.value].normal;
        return (
          <BubbleCommon
            key={option.value}
            tipsMap={option.tips}
            showIcon={false}
            placement={index === 0 ? "right" : 'top'}
          >
            <div
              className={classNames('dbom-ct-status-tab', {
                '--active': isActive,
              })}
              onClick={() => onChange(option.value)}
            >
              <img
                src={icon}
                className="dbom-ct-status-tab__icon"
                alt=""
              />
              <span className="dbom-ct-status-tab__text">
                {intl(option.text)} ({counts?.[option.value] || 0})
              </span>
            </div>
          </BubbleCommon>
        );
      })}
    </div>
  );
}
